import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { usePostRegisterUserData, useProfileImageUpload } from "../../../../api/userDetails";
import { successMessage } from "../../../../helpers/ErrorHandler";
import { getUserRolesInLocal } from "../../../../utils/cacheStorage";
import { buildRegisterUserPayload } from "../../shared/services/userPayload";
import { createRoleSelectionPolicy } from "../../shared/services/userRolePolicy";
import { UserFormData } from "../../shared/types/user.types";
import { UserRegisterModalProps, UserRegisterViewModel } from "../types/userRegisterView.types";

/**
 * Ports the submit logic of the legacy `UserRegisterModal`
 * (src/screens/user/userRegister/UserRegisterModal.tsx): same POST /user
 * payload (`status: ACTIVE`, role as a one-item array — or the plain
 * "Supplier" string for a supplier-role user), same success toast. The
 * supplier-vs-standard role rule lives in a `RoleSelectionPolicy`.
 */
export const useUserRegisterViewModel = ({
  visibleModal,
  closeModal,
  onSuccess
}: Omit<UserRegisterModalProps, "roles">): UserRegisterViewModel => {
  const rolePolicy = useMemo(() => createRoleSelectionPolicy(getUserRolesInLocal()?.[0]), []);
  const { isLoading: isCreating, mutate: registerUser } = usePostRegisterUserData();
  const { isLoading: isUploading, mutate: uploadProfileImage } = useProfileImageUpload();

  const [pictureFile, setPictureFile] = useState<File | null>(null);
  const [pictureUrl, setPictureUrl] = useState<string | null>(null);

  // Local preview of the picked picture; revoke the blob URL when replaced/cleared.
  useEffect(() => {
    if (!pictureFile) {
      setPictureUrl(null);
      return;
    }
    const url = URL.createObjectURL(pictureFile);
    setPictureUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pictureFile]);

  const defaultValues = useMemo<UserFormData>(
    () => ({ firstName: "", lastName: "", email: "", phone: "", role: rolePolicy.defaultRole }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleModal, rolePolicy]
  );

  const methods = useForm<UserFormData>({ mode: "all", defaultValues, values: defaultValues });
  const { handleSubmit, reset } = methods;

  useEffect(() => {
    return () => {
      setPictureFile(null);
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleModal]);

  const finish = (message: string) => {
    successMessage(message);
    closeModal();
    onSuccess();
  };

  const onSubmit = (data: UserFormData) => {
    registerUser(buildRegisterUserPayload(data, rolePolicy), {
      onSuccess: (response: any) => {
        const userId = response?.data?._id ?? response?._id;
        if (pictureFile && userId) {
          uploadProfileImage(
            { userId, file: pictureFile },
            {
              onSuccess: () => finish("User register successfully"),
              onError: () => finish("User registered successfully (picture upload failed)")
            }
          );
        } else {
          finish("User register successfully");
        }
      }
    });
  };

  const onCancel = () => {
    closeModal();
    reset();
  };

  return {
    methods,
    rolePolicy,
    pictureUrl,
    onPictureSelect: setPictureFile,
    isSaving: isCreating || isUploading,
    onSave: handleSubmit(onSubmit),
    onCancel
  };
};
